import { useState } from "react";
import { Loader as Loader2 } from "lucide-react";
import { useContacts } from "@/hooks/useContacts";
import { useAssistants } from "@/hooks/useAssistants";
import { useTeam } from "@/hooks/useTeam";
import { useAuth } from "@/contexts/AuthContext";
import { clampAttributionIdForSave } from "@/lib/chatContactScope";
import { MobileSheet } from "./MobileSheet";
import { MobileSelect, MobileFieldLabel, mobileInputClass } from "./MobileSelect";

const COUNTRY_CODES = [
  { code: "55", label: "🇧🇷 +55" },
  { code: "1", label: "🇺🇸 +1" },
  { code: "351", label: "🇵🇹 +351" },
  { code: "44", label: "🇬🇧 +44" },
  { code: "34", label: "🇪🇸 +34" },
  { code: "49", label: "🇩🇪 +49" },
  { code: "33", label: "🇫🇷 +33" },
  { code: "39", label: "🇮🇹 +39" },
  { code: "52", label: "🇲🇽 +52" },
  { code: "54", label: "🇦🇷 +54" },
];

/** Monta o JID do WhatsApp igual ao fluxo do web (AddNewContact). */
const buildJid = (phone: string, countryCode: string) => {
  const digits = phone.replace(/\D/g, "");
  const withCountry = digits.startsWith(countryCode) ? digits : `${countryCode}${digits}`;
  return { jid: `${withCountry}@s.whatsapp.net`, phone: withCountry };
};

const NewContactForm = ({ onCreated }: { onCreated: () => void }) => {
  const { user } = useAuth();
  const { createContact } = useContacts();
  const { assistants } = useAssistants();
  const { members } = useTeam();

  const canAssignToOthers = user?.can_assign_contacts === true || user?.type === "agency_user";

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [countryCode, setCountryCode] = useState("55");
  const [assistantId, setAssistantId] = useState("none");
  const [attributionId, setAttributionId] = useState(
    canAssignToOthers ? "none" : user?.auth_user_id || "none"
  );
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ type: "error" | "success"; text: string } | null>(null);

  const handleSave = async () => {
    if (!name.trim()) {
      setFeedback({ type: "error", text: "Nome do contato é obrigatório" });
      return;
    }
    if (!phone.trim()) {
      setFeedback({ type: "error", text: "Telefone é obrigatório" });
      return;
    }

    setSaving(true);
    setFeedback(null);

    const { jid, phone: fullPhone } = buildJid(phone, countryCode);
    const selectedAssistant = assistantId === "none" ? null : assistantId;

    const result = await createContact({
      jid,
      phone: fullPhone,
      display_name: name.trim(),
      name: name.trim(),
      push_name_last: name.trim(),
      email: email.trim() || "",
      is_group: false,
      account_assistant_id: selectedAssistant,
      assistant_is_active: !!selectedAssistant,
      attribution_id: clampAttributionIdForSave(
        attributionId === "none" ? null : attributionId,
        user ?? null
      ),
      last_message_id: null,
      last_message_at: null,
      profile_picture: null,
      profile_picture_updated_at: null,
    });

    setSaving(false);

    if (result.success) {
      setFeedback({ type: "success", text: "Contato criado com sucesso!" });
      setTimeout(onCreated, 900);
    } else {
      setFeedback({ type: "error", text: result.error || "Não foi possível criar o contato." });
    }
  };

  return (
    <div className="flex flex-col gap-4 pb-2">
      {feedback && (
        <p
          className={`rounded-xl px-3 py-2.5 text-[13px] ${
            feedback.type === "success"
              ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
              : "bg-red-500/10 text-red-600 dark:text-red-400"
          }`}
        >
          {feedback.text}
        </p>
      )}

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>Nome *</MobileFieldLabel>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Nome do contato"
          className={mobileInputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>Telefone *</MobileFieldLabel>
        <div className="flex gap-2">
          <div className="w-[104px] shrink-0">
            <MobileSelect value={countryCode} onChange={setCountryCode} ariaLabel="Código do país">
              {COUNTRY_CODES.map((country) => (
                <option key={country.code} value={country.code}>
                  {country.label}
                </option>
              ))}
            </MobileSelect>
          </div>
          <input
            type="tel"
            inputMode="numeric"
            value={phone}
            onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
            placeholder="11999999999"
            className={mobileInputClass}
          />
        </div>
        <p className="text-[12px] text-muted-foreground">Apenas números, com DDD.</p>
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>E-mail</MobileFieldLabel>
        <input
          type="email"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@exemplo.com"
          className={mobileInputClass}
        />
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>Assistente</MobileFieldLabel>
        <MobileSelect value={assistantId} onChange={setAssistantId} ariaLabel="Assistente">
          <option value="none">Nenhum assistente</option>
          {assistants
            .filter((a) => a.is_active)
            .map((assistant) => (
              <option key={assistant.id} value={assistant.id}>
                {assistant.name}
              </option>
            ))}
        </MobileSelect>
      </div>

      <div className="flex flex-col gap-2">
        <MobileFieldLabel>Atribuir para</MobileFieldLabel>
        <MobileSelect
          value={attributionId}
          onChange={setAttributionId}
          disabled={!canAssignToOthers}
          ariaLabel="Usuário atribuído"
        >
          <option value="none">Nenhum responsável</option>
          {members
            .filter((m) => m.is_active && m.auth_user_id)
            .filter((m) => (canAssignToOthers ? true : m.auth_user_id === user?.auth_user_id))
            .map((member) => (
              <option key={member.auth_user_id} value={member.auth_user_id!}>
                {member.name || member.email}
              </option>
            ))}
        </MobileSelect>
        {!canAssignToOthers && (
          <p className="text-[12px] text-muted-foreground">
            Você só pode atribuir contatos a si mesmo.
          </p>
        )}
      </div>

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="mt-1 flex h-12 items-center justify-center gap-2 rounded-xl bg-accent-blue text-[15px] font-semibold text-white transition-opacity active:opacity-80 disabled:opacity-50"
      >
        {saving && <Loader2 className="h-4 w-4 animate-spin" />}
        {saving ? "Salvando..." : "Criar contato"}
      </button>
    </div>
  );
};

interface MobileNewContactSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const MobileNewContactSheet = ({ open, onOpenChange }: MobileNewContactSheetProps) => {
  return (
    <MobileSheet open={open} onOpenChange={onOpenChange} title="Novo contato">
      {open && <NewContactForm onCreated={() => onOpenChange(false)} />}
    </MobileSheet>
  );
};
